/**
 * `/hottie` deep-link helpers.
 *
 * Hold-the-Orb opens the in-kit HottieSheet inside OrbFan/OrbRail. This
 * module is only for the URL siblings still hand out, and for remembering
 * where they came from. It must not navigate, and it must not open a second
 * dock.
 */

import { ROUTES } from '@/config/routes';

/** Surfaces that exist before you are "in" — Orb is suppressed, so the sheet cannot open. */
const NO_SHEET: ReadonlySet<string> = new Set<string>([
  ROUTES.landing,
  ROUTES.login,
  ROUTES.signup,
  ROUTES.profileBuilder,
  ROUTES.verify,
  ROUTES.verifyAge,
  ROUTES.hottie,
]);

/**
 * Where `/hottie` should land so the in-kit sheet can open over a real page.
 *
 * Never uses `return_to` — that query is handed out across the ecosystem and
 * auto-navigating it would make this origin an open redirector.
 */
export function hottieLandingPath(referrer: string | null, origin: string): string {
  if (!referrer) return ROUTES.feed;
  try {
    const url = new URL(referrer, origin);
    if (url.origin !== origin) return ROUTES.feed;
    if (NO_SHEET.has(url.pathname) || url.pathname.startsWith('/auth')) {
      return ROUTES.feed;
    }
    return `${url.pathname}${url.search}`;
  } catch {
    return ROUTES.feed;
  }
}

/** Location state that tells the chassis to open the in-kit sheet. */
export const HOTTIE_SHEET_STATE = { openHottieSheet: true } as const;

const RETURN_KEY = 'hottr.hottie.returnTo';

/**
 * Remember which sibling site sent the user here, so Hottie can offer a way
 * back after answering.
 *
 * STORED, NEVER AUTO-NAVIGATED. `/hottie?return_to=` is a URL handed out across
 * the whole ecosystem, so anyone can craft one. Redirecting to it automatically
 * would make hottr.app an open redirector wearing its own domain — the most
 * convincing possible phishing hop. Only same-scheme HOTTR origins are kept at
 * all, and even those are surfaced as a link the user chooses to press.
 */
export function setHottieReturnTo(url: string | null, from: string | null): void {
  if (!url) return;
  try {
    const parsed = new URL(url);
    const isHottr =
      parsed.protocol === 'https:' &&
      (parsed.hostname === 'hottr.world' || /(^|\.)hottr\.[a-z]+$/i.test(parsed.hostname));
    if (!isHottr) return;
    sessionStorage.setItem(RETURN_KEY, JSON.stringify({ url: parsed.toString(), from }));
  } catch {
    /* malformed URL — drop it */
  }
}

/** The remembered origin, or null. Single use. */
export function takeHottieReturnTo(): { url: string; from: string | null } | null {
  try {
    const raw = sessionStorage.getItem(RETURN_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(RETURN_KEY);
    return JSON.parse(raw) as { url: string; from: string | null };
  } catch {
    return null;
  }
}

/**
 * Which mounted chassis owns the live HOTTIE item.
 *
 * Only OrbFan's dock is mounted now — there is no desktop rail — so the
 * dock is the chassis. Null means nothing has painted yet; the caller
 * should wait a frame rather than click a control that is not on screen.
 */
export function hottieSheetChassis(
  railDisplayed: boolean,
  dockDisplayed: boolean,
): 'rail' | 'dock' | null {
  if (railDisplayed === dockDisplayed) return null;
  return railDisplayed ? 'rail' : 'dock';
}

/**
 * Click the visible chassis HOTTIE item so the in-kit sheet opens.
 *
 * The fan's HOTTIE item is the opener at every width. It is in the DOM
 * even while the fan is closed. Retry a
 * few frames if the chassis has not painted yet (hard refresh on
 * `/hottie` → `/feed`).
 */
export function openInKitHottieSheet(): void {
  if (typeof document === 'undefined') return;

  const clickVisible = (): boolean => {
    const btn = visibleHottieItem();
    if (!btn) return false;
    btn.click();
    return true;
  };

  if (clickVisible()) return;

  let frames = 0;
  const retry = () => {
    if (clickVisible() || ++frames >= 12) return;
    window.requestAnimationFrame(retry);
  };
  window.requestAnimationFrame(retry);
}

function visibleHottieItem(): HTMLButtonElement | null {
  // No .hottr-rail lookup: the rail is retired, so querying it could only
  // ever miss and leave a branch nobody can reach. The dock is the chassis.
  const dock = document.querySelector<HTMLElement>('.hottr-dock');
  if (!dock || window.getComputedStyle(dock).display === 'none') return null;
  return dock.querySelector<HTMLButtonElement>('[data-route="hottie"]');
}
